/** Tiered Cart Drawer for Shopify Online Store 2.0.
 * Locale-aware Ajax Cart API, no checkout price manipulation.
 * Horizon 3.x-koppeling: luistert op het theme-event `cart:update` (product-form-component, quick-add,
 * sticky ATC) en stuurt na eigen wijzigingen hetzelfde event terug zodat de header-bubble synchroon blijft.
 */
(() => {
  'use strict';
  if (window.TieredCart && window.TieredCart.__mounted) return;
  const drawer = document.querySelector('[data-tcd-drawer]');
  if (!drawer) return;
  const backdrop = document.querySelector('[data-tcd-backdrop]');
  const $ = (selector) => drawer.querySelector(selector);
  const $$ = (selector) => Array.from(drawer.querySelectorAll(selector));
  const root = String(drawer.dataset.tcdRoot || window.Shopify?.routes?.root || '/').replace(/\/*$/, '/');
  const promotionVerified = drawer.dataset.tcdPromotionEnabled === 'true';
  const locale = drawer.dataset.tcdLocale || document.documentElement.lang || 'en-US';
  const shopCurrency = drawer.dataset.tcdCurrency || 'USD';
  const parseList = (value) => String(value || '').split(',').map((s) => Number(s.trim())).filter((n) => Number.isFinite(n) && n > 0);
  const thresholds = parseList(drawer.dataset.tcdThresholds);
  const percentages = parseList(drawer.dataset.tcdPercentages);
  const milestones = thresholds.length && thresholds.length === percentages.length
    ? thresholds.map((items, i) => ({ items, off: percentages[i] }))
    : [];
  const htmlEscape = (value) => String(value ?? '').replace(/[&<>"']/g, (ch) => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[ch]));
  const safeImage = (value) => {
    if (!value) return '';
    try { const u = new URL(value, location.href); return ['https:','http:'].includes(u.protocol) ? u.href : ''; } catch { return ''; }
  };
  const money = (amount, currency) => {
    const code = typeof currency === 'string' ? currency : (currency?.iso_code || shopCurrency);
    try { return new Intl.NumberFormat(locale, {style:'currency',currency:code,minimumFractionDigits:2,maximumFractionDigits:2}).format((Number(amount)||0)/100); }
    catch { return `${code} ${((Number(amount)||0)/100).toFixed(2)}`; }
  };
  const notify = (message) => { const live = $('[data-tcd-live]'); if (live) live.textContent = message; };
  const showError = (message = '') => { const el = $('[data-tcd-error]'); if (el) {el.textContent = message; el.classList.toggle('is-visible', Boolean(message));} if (message) notify(message); };
  let cart = null, busy = false, opened = false, lastFocus = null, index = 0;
  async function ajax(path, options = {}) {
    const res = await fetch(root + path, {credentials:'same-origin',headers:{'Accept':'application/json',...(options.body ? {'Content-Type':'application/json'} : {})},...options});
    if (!res.ok) {
      let error = `Cart request failed (${res.status}).`;
      try { const body = await res.json(); error = body.description || body.message || error; } catch {}
      throw new Error(error);
    }
    return res.json();
  }
  async function refresh() {
    cart = await ajax('cart.js');
    render();
    return cart;
  }
  function tierFill(quantity) {
    if (quantity <= 0 || !milestones.length) return 0;
    const n = milestones.length;
    if (quantity >= milestones[n - 1].items) return 100;
    const points = [{count:0,position:0}].concat(milestones.map((m, i) => ({count:m.items, position:(i + 1) / n * 100})));
    for (let i = 1; i < points.length; i++) if (quantity <= points[i].count) {
      const a = points[i-1], b = points[i];
      return a.position + (quantity - a.count) / (b.count - a.count) * (b.position - a.position);
    }
    return 100;
  }
  function renderTier(quantity) {
    const el=$('[data-tcd-tier]');if (!el || !milestones.length) return;
    const heading=$('[data-tcd-tier-heading]');
    const next=milestones.find(level=>quantity<level.items);
    const current=[...milestones].reverse().find(level=>quantity>=level.items);
    if (promotionVerified) {
      if (!next) heading.innerHTML=`You’ve unlocked <strong>${current.off}% off!</strong>`;
      else if (current) heading.innerHTML=`You’ve unlocked <strong>${current.off}% off</strong> · Add ${next.items-quantity} more for <strong>${next.off}% off</strong>`;
      else heading.innerHTML=`Add ${next.items-quantity} more and receive <strong>${next.off}% off</strong>`;
    } else {
      // Never promise discounts that haven't been activated in Shopify Admin.
      heading.textContent = next ? `Add ${next.items-quantity} more to reach ${next.items} items` : `You’ve reached ${milestones[milestones.length-1].items} items`;
    }
    const fill=$('[data-tcd-tier-fill]');if(fill)fill.style.width=tierFill(quantity)+'%';
    $$('[data-tcd-threshold]').forEach((stop)=>stop.classList.toggle('is-achieved',quantity>=Number(stop.dataset.tcdThreshold)));
    el.dataset.unverified=String(!promotionVerified);
    $$('[data-tcd-threshold] .tcd-tier-bottom').forEach((label,i)=>{label.textContent = promotionVerified ? milestones[i].off+'% OFF' : 'NOT ACTIVE';});
  }
  function itemMarkup(item, currency) {
    const key=htmlEscape(item.key);
    const quantity=Number(item.quantity)||1;
    const hasDiscount=Number(item.original_line_price)>Number(item.final_line_price);
    const name=htmlEscape(item.product_title || item.title || 'Product');
    const url=htmlEscape(item.url || '#');
    const image=safeImage(item.image || item.featured_image?.url || '');
    let options = Array.isArray(item.options_with_values) ? item.options_with_values.filter(opt=>opt.value&&opt.value!=='Default Title') : [];
    if (!options.length && Array.isArray(item.variant_options)) options=item.variant_options.filter(v=>v&&v!=='Default Title').map((v,i)=>({name:'Option '+(i+1),value:v}));
    const optionHtml=options.map(opt=>`<div>${htmlEscape(opt.name)}: ${htmlEscape(opt.value)}</div>`).join('');
    const properties=Object.entries(item.properties || {}).filter(([k,v])=>!k.startsWith('_')&&v!==null&&v!==''&&typeof v!=='object').map(([k,v])=>`<div>${htmlEscape(k)}: ${htmlEscape(v)}</div>`).join('');
    const price=money(item.final_line_price ?? item.line_price ?? (item.price*quantity),currency);
    const original=hasDiscount ? `<span class="tcd-was-price">${money(item.original_line_price,currency)}</span>` : '';
    return `<article class="tcd-item" data-tcd-item-key="${key}">
      ${image?`<img class="tcd-item-image" src="${htmlEscape(image)}" alt="${name}" loading="lazy" width="100" height="100">`:`<div class="tcd-item-img-placeholder" aria-hidden="true">◇</div>`}
      <div class="tcd-item-info"><a class="tcd-item-name" href="${url}">${name}</a>
      <div class="tcd-item-options">${optionHtml}${properties}</div>
      <div class="tcd-item-bottom"><div class="tcd-qty" aria-label="Quantity for ${name}">
      <button type="button" class="tcd-qty-btn" aria-label="Decrease quantity" data-tcd-adjust="-1" data-tcd-key="${key}">−</button>
      <span class="tcd-qty-count">${quantity}</span>
      <button type="button" class="tcd-qty-btn" aria-label="Increase quantity" data-tcd-adjust="1" data-tcd-key="${key}">+</button>
      </div><div class="tcd-line-price">${original}${price}</div></div></div>
      <button type="button" class="tcd-remove" aria-label="Remove ${name}" data-tcd-remove="${key}">×</button>
    </article>`;
  }
  function render() {
    if(!cart) return;
    const count=Number(cart.item_count)||0;
    const currency=cart.currency || shopCurrency;
    const countEl=$('[data-tcd-count]');if(countEl)countEl.textContent=String(count);
    const itemsEl=$('[data-tcd-items]');
    if (itemsEl) itemsEl.innerHTML=count===0 ? `<div class="tcd-empty"><h3>Your cart is empty</h3><p>Discover your next favourite.</p><a href="${htmlEscape(drawer.dataset.tcdContinueUrl || '/')}">Continue shopping</a></div>` : cart.items.map(item=>itemMarkup(item,currency)).join('');
    const total=$('[data-tcd-total]');if(total)total.textContent=money(cart.total_price,currency);
    const discount=$('[data-tcd-discount]');
    if(discount){discount.hidden=!(cart.total_discount>0);discount.textContent=cart.total_discount>0 ? `Discounts applied: −${money(cart.total_discount,currency)}`:'';}
    const checkout=$('[data-tcd-checkout]');if(checkout){checkout.setAttribute('aria-disabled',String(count===0||busy)); checkout.tabIndex=count===0||busy?-1:0;}
    renderTier(count);
    document.querySelectorAll('[data-tcd-cart-count]').forEach(el=>{el.textContent=count;});
    notify(`Cart updated, ${count} item${count===1?'':'s'}, total ${money(cart.total_price,currency)}`);
    window.dispatchEvent(new CustomEvent('tiered-cart:updated',{detail:{cart}}));
  }
  // Horizon: zelfde event als product-form-component, zodat cart-icon (bubble) de teller bijwerkt.
  function emitThemeUpdate() {
    if (!cart) return;
    const themeEvent = new Event('cart:update', {bubbles:true});
    themeEvent.detail = {resource:cart, sourceId:'tiered-cart', data:{source:'tiered-cart', itemCount:cart.item_count}};
    document.dispatchEvent(themeEvent);
  }
  function setBusy(next){busy=next;drawer.classList.toggle('is-busy',busy);$$('[data-tcd-adjust],[data-tcd-remove],[data-tcd-add]').forEach(btn=>btn.disabled=busy);const checkout=$('[data-tcd-checkout]');if(checkout)checkout.setAttribute('aria-disabled',String(busy||!cart?.item_count));}
  async function mutation(action){if(busy)return;setBusy(true);showError('');try{await action();await refresh();emitThemeUpdate();}catch(e){showError(e?.message||'Sorry, your cart could not be updated. Please retry.');try{await refresh();}catch{}}finally{setBusy(false);render();}}
  async function change(key,quantity){
    await ajax('cart/change.js',{method:'POST',body:JSON.stringify({id:key,quantity})});
  }
  async function add(variantId){
    await ajax('cart/add.js',{method:'POST',body:JSON.stringify({items:[{id:Number(variantId),quantity:1}]})});
  }
  async function open(){
    if(opened)return;
    lastFocus=document.activeElement;
    opened=true;drawer.classList.add('is-open');backdrop?.classList.add('is-open');
    drawer.setAttribute('aria-hidden','false');document.body.classList.add('tcd-lock');
    drawer.querySelector('[data-tcd-close]')?.focus({preventScroll:true});
    try{await refresh();showError('');}catch(e){showError(e?.message||'Unable to load cart.');}
  }
  function close(){
    if(!opened)return;opened=false;drawer.classList.remove('is-open');backdrop?.classList.remove('is-open');
    drawer.setAttribute('aria-hidden','true');document.body.classList.remove('tcd-lock');
    if(lastFocus&&typeof lastFocus.focus==='function')lastFocus.focus({preventScroll:true});
  }
  function moveUpsell(direction){const slides=$$('[data-tcd-slide]');if(!slides.length)return;index=(index+direction+slides.length)%slides.length;slides.forEach((slide,i)=>slide.hidden=i!==index);const single=slides.length<=1;$$('[data-tcd-prev],[data-tcd-next]').forEach(btn=>btn.hidden=single);}
  function initUpsells(){const slides=$$('[data-tcd-slide]');const upsell=$('[data-tcd-upsells]');if(upsell&&!slides.length)upsell.hidden=true;else moveUpsell(0);}
  drawer.addEventListener('click',event=>{
    const btn=event.target.closest('button,a');if(!btn)return;
    if(btn.matches('[data-tcd-close]')){close();return;}
    if(btn.matches('[data-tcd-prev]')){moveUpsell(-1);return;}
    if(btn.matches('[data-tcd-next]')){moveUpsell(1);return;}
    if(btn.matches('[data-tcd-adjust]')){const item=cart?.items.find(item=>item.key===btn.dataset.tcdKey);if(item)mutation(()=>change(item.key,Math.max(0,item.quantity+Number(btn.dataset.tcdAdjust))));return;}
    if(btn.matches('[data-tcd-remove]')){mutation(()=>change(btn.dataset.tcdRemove,0));return;}
    if(btn.matches('[data-tcd-add]')){mutation(()=>add(btn.dataset.tcdAdd));return;}
    if(btn.matches('[data-tcd-checkout]')&&(busy||!cart?.item_count)){event.preventDefault();return;}
  });
  backdrop?.addEventListener('click',close);
  document.addEventListener('keydown',event=>{
    if(!opened)return;
    if(event.key==='Escape'){event.preventDefault();close();return;}
    if(event.key!=='Tab')return;
    const focusables=$$('button:not([disabled]):not([hidden]),a[href]:not([aria-disabled="true"]),[tabindex]:not([tabindex="-1"])').filter(el=>!el.closest('[hidden]')&&el.getClientRects().length);
    if(!focusables.length){event.preventDefault();drawer.focus();return;}
    const first=focusables[0],last=focusables[focusables.length-1];
    if(event.shiftKey&&(document.activeElement===first||document.activeElement===drawer)){event.preventDefault();last.focus();}
    else if(!event.shiftKey&&document.activeElement===last){event.preventDefault();first.focus();}
  });
  document.addEventListener('click',event=>{
    const target=event.target.closest('[data-tcd-open],a[href]');if(!target||drawer.contains(target))return;
    const manual=target.hasAttribute('data-tcd-open');
    let isCart=false;
    if(!manual&&target.tagName==='A')try{isCart=/\/cart\/?$/.test(new URL(target.href,location.href).pathname);}catch{}
    if(!manual&&!isCart)return;
    event.preventDefault();event.stopImmediatePropagation();open();
  },true);
  document.addEventListener('tiered-cart:open',()=>open());
  document.addEventListener('tiered-cart:refresh',()=>refresh().catch(e=>showError(e.message)));
  // Horizon 3.x: product-form-component / quick-add / sticky ATC posten zelf naar /cart/add.js en
  // dispatchen daarna `cart:update`. Wij verversen en openen alleen bij een echte add vanuit het productformulier.
  document.addEventListener('cart:update',event=>{
    const data=event?.detail?.data||{};
    if(data.source==='tiered-cart')return; // eigen event, voorkomt een lus
    const fromProductForm=data.source==='product-form-component';
    refresh().then(()=>{ if(fromProductForm&&!data.didError) open(); }).catch(e=>showError(e?.message||''));
  });
  // Opt-in only! Themes with built-in AJAX handlers must use their native 'added' hook.
  document.addEventListener('submit',event=>{
    const form=event.target.closest('form[data-tcd-ajax-add]');if(!form)return;
    event.preventDefault();event.stopImmediatePropagation();
    const data=new FormData(form);const variant=data.get('id');const quantity=Math.max(1,Number(data.get('quantity')||1));
    if(!variant)return;
    mutation(async()=>{
      await ajax('cart/add.js',{method:'POST',body:JSON.stringify({items:[{id:Number(variant),quantity}]})});
    }).then(()=>open());
  },true);
  window.TieredCart={__mounted:true,open,close,refresh,addVariant:async(id)=>{await mutation(()=>add(id));open();}};
  initUpsells();
})();
